/*------------------------------------------------------------------------------------------
* © 2020 KPMG LLP, a Delaware limited liability partnership and 
* a member firm of the KPMG global organization of independent member firms 
* affiliated with KPMG International Limited, a private English company limited by guarantee. 
* All rights reserved.
*-----------------------------------------------------------------------------------------*/

import { AfterViewInit, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { NavigationEnd, NavigationError, Router } from '@angular/router';
import { MsalBroadcastService, MsalGuardConfiguration, MsalService, MSAL_GUARD_CONFIG } from '@azure/msal-angular';
import { EventMessage, EventType } from '@azure/msal-browser';

import * as moment from 'moment';
import { filter, Subscription } from 'rxjs';
import { ThemeLoaderService } from '@kpmg-ux/ngx-tax-design-system';
import { CommonService } from 'src/app/services/common.service';
import { AppContextService } from './core/services/app-context.service';
import { EnvSettingsService } from './core/services/env-settings.service';
import { StorageKey } from './models/application-enum.model';
import { UserActivityLogs } from './models/user-activity-tracking.model';
import { ProgressIndicatorService } from './shared/progress-indicator/progress-indicator.service';

/** Default landing page route (Angular path — no base href prefix). */
const LANDING_ROUTE = '/evs/home';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  private _subscriptions: Subscription[] = [];
  adalConfig: any;
  title = '10-K Spotlight';
  isApplicationSetUp = false;
  isAdminScreen = false;
  alertUserLogOut = false;
  public timeSpentOnPages: UserActivityLogs[] = [];
  moment = moment;
  previousUrl: string | null = null;

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    public envSettings: EnvSettingsService,
    private _appContext: AppContextService,
    private _progressIndicator: ProgressIndicatorService,
    private _commonService: CommonService,
    private cdRef: ChangeDetectorRef,
    private _router: Router,
    private _themeLoaderService: ThemeLoaderService,
    private _location: Location
  ) {
    // Fix double base-href: /spotlight/spotlight/... → /spotlight/evs/home
    // Runs before any Angular lifecycle, guards, or MSAL logic.
    if (window.location.href.includes('/spotlight/spotlight')) {
      window.location.replace(window.location.origin + '/spotlight/evs/home');
      return;
    }

    // React to successful MSAL login events
    this.msalBroadcastService.msalSubject$
      .pipe(filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS))
      .subscribe((result: any) => {
        if (result.payload) {
          this.msalService.instance.setActiveAccount(result.payload.account);
          this.isApplicationSetUp = true;
          this._appContext.initialize();
        }
      });

    this.adalConfig = this.envSettings.getAzureConfig();

    // Track page time and handle stale-chunk errors on every navigation
    this._router.events.subscribe((event: any) => {
      if (event instanceof NavigationEnd) {
        const currentUrl = event.url;
        if (this.previousUrl && this.previousUrl !== currentUrl) {
          this._commonService.endPageTimer(this.previousUrl, this.msalService.instance.getActiveAccount()?.username ?? '');
          this._commonService.startPageTimer();
        } else {
          this._commonService.startPageTimer();
        }
        this.previousUrl = currentUrl;
      }
      if (event instanceof NavigationError && event.error?.name === 'ChunkLoadError') {
        if (!sessionStorage.getItem('chunk_reload_attempted')) {
          sessionStorage.setItem('chunk_reload_attempted', '1');
          window.location.reload();
        }
      }
    });
  }

  appDestroyEvent(): void {
    this.timeSpentOnPages = this._commonService.endPageTimer(
      this.previousUrl ?? '',
      this.msalService.instance.getActiveAccount()?.username ?? ''
    );
    this._commonService.storeUserActivityLogs(
      this.timeSpentOnPages,
      this.msalService.instance.getActiveAccount()?.username ?? ''
    ).subscribe();
  }

  ngOnInit(): void {
    this._progressIndicator.show(true);

    // MSAL does not auto-restore the active account on page refresh — pull it from cache.
    if (!this.msalService.instance.getActiveAccount()) {
      const accounts = this.msalService.instance.getAllAccounts();
      if (accounts.length > 0) {
        this.msalService.instance.setActiveAccount(accounts[0]);
      }
    }
    this.isApplicationSetUp = this.msalService.instance.getActiveAccount() != null;

    // Persist the Angular route in sessionStorage after every navigation.
    // Skipped while a MSAL redirect is in progress so that the redirect callback URL
    // does not overwrite the route the user actually wanted to visit.
    // sessionStorage survives same-tab redirects (including MSAL's redirect flow).
    this._router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this._appContext.refreshOutageMessage();
        if (sessionStorage.getItem(StorageKey.IsRefreshToken) !== 'true') {
          const url = event.url;
          const qs = url.includes('?') ? url.substring(url.indexOf('?')) : '';
          const path = url.replace(qs, '');
          // Do not persist error or access-denied routes
          if (path && path !== '/' && !path.startsWith('/access-denied') && !path.startsWith('/app-error')) {
            sessionStorage.setItem(StorageKey.AngularRoute, path);
            sessionStorage.setItem(StorageKey.QueryString, qs);
          }
        }
      }
    });

    localStorage.setItem('termsAccepted', 'false');

    this._themeLoaderService.theme$.subscribe(theme => {
      const link = document.getElementById('app-theme-stylesheet') as HTMLLinkElement;
      if (link) {
        link.href = `css/app${theme.charAt(0).toUpperCase() + theme.slice(1)}.min.css`;
      }
    });
  }

  ngAfterViewInit(): void {
    const isLoggedIn = this.msalService.instance.getActiveAccount() != null;
    const isPostMsalRedirect = sessionStorage.getItem(StorageKey.IsRefreshToken) === 'true';

    if (!isLoggedIn) {
      // Not yet authenticated.
      // Capture the intended deep-link using Location.path(), which correctly strips
      // baseHref in every environment:
      //   Local (baseHref='/')         → '/evs/home'
      //   QA    (baseHref='/spotlight/')→ '/evs/home'  (never '/spotlight/evs/home')
      // sessionStorage persists through the MSAL same-tab redirect so the route survives.
      const intendedPath = this._location.path(true);
      if (intendedPath && intendedPath !== '/') {
        const qs = intendedPath.includes('?') ? intendedPath.substring(intendedPath.indexOf('?')) : '';
        sessionStorage.setItem(StorageKey.AngularRoute, intendedPath.split('?')[0]);
        sessionStorage.setItem(StorageKey.QueryString, qs);
      }
      // Mark that we are entering the MSAL redirect flow
      sessionStorage.setItem(StorageKey.IsRefreshToken, 'true');
      // MsalGuard (applied on routes) will handle the redirect to Azure AD
      return;
    }

    if (isPostMsalRedirect) {
      // Returning from MSAL login redirect — restore the intended route.
      // Fall back to the default landing page if nothing was saved.
      const savedRoute = sessionStorage.getItem(StorageKey.AngularRoute) || '';
      const savedQuery = sessionStorage.getItem(StorageKey.QueryString) || '';
      sessionStorage.removeItem(StorageKey.AngularRoute);
      sessionStorage.removeItem(StorageKey.QueryString);
      sessionStorage.removeItem(StorageKey.IsRefreshToken);
      this._router.navigateByUrl((savedRoute || LANDING_ROUTE) + savedQuery);
    } else {
      // Normal load or browser refresh while already authenticated.
      // If the browser is sitting at the base path ('/'), send the user to the landing page.
      const currentPath = this._location.path();
      if (!currentPath || currentPath === '/') {
        this._router.navigateByUrl(LANDING_ROUTE);
      }
      // Otherwise Angular's PathLocationStrategy already parsed the URL correctly,
      // so the router will render the right component — no extra navigation needed.
    }
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(s => s.unsubscribe());
  }

  setAdminFlag(): void {
    this.isAdminScreen = !!this._commonService.getAppId();
    this.cdRef.detectChanges();
  }
}
